import { useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ensure credentials are always sent
axios.defaults.withCredentials = true;

export function useNaraApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const searchRecords = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams();
      if (params.q) queryParams.set("q", params.q);
      if (params.limit) queryParams.set("limit", params.limit);
      if (params.offset !== undefined) queryParams.set("offset", params.offset);
      if (params.available_online !== null && params.available_online !== undefined)
        queryParams.set("available_online", params.available_online);
      if (params.type_of_materials) queryParams.set("type_of_materials", params.type_of_materials);
      if (params.result_types) queryParams.set("result_types", params.result_types);

      const res = await axios.get(`${API}/nara/search?${queryParams.toString()}`);
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Search failed";
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getRecord = useCallback(async (naId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/nara/records/${naId}`);
      return res.data;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to fetch record");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/nara/status`);
      return res.data;
    } catch {
      return { hasApiKey: false, mode: "demo" };
    }
  }, []);

  const saveRecord = useCallback(async (record) => {
    try {
      await axios.post(`${API}/saved`, {
        naid: record.naId,
        title: record.title,
        description: record.description,
        record_type: record.recordType,
        date_range: record.dateRange,
        thumbnail_url: record.thumbnailUrl,
        has_online_content: record.hasOnlineContent,
        raw_data: record,
      });
      return true;
    } catch (err) {
      if (err.response?.status === 409) return true;
      return false;
    }
  }, []);

  const unsaveRecord = useCallback(async (naid) => {
    try {
      await axios.delete(`${API}/saved/${naid}`);
      return true;
    } catch {
      return false;
    }
  }, []);

  const getSavedRecords = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/saved`);
      return res.data;
    } catch {
      return [];
    }
  }, []);

  const checkSaved = useCallback(async (naid) => {
    try {
      const res = await axios.get(`${API}/saved/check/${naid}`);
      return res.data.saved;
    } catch {
      return false;
    }
  }, []);

  const getSearchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/history?limit=15`);
      return res.data;
    } catch {
      return [];
    }
  }, []);

  const clearHistory = useCallback(async () => {
    try {
      await axios.delete(`${API}/history`);
      return true;
    } catch {
      return false;
    }
  }, []);

  return {
    loading,
    error,
    searchRecords,
    getRecord,
    getStatus,
    saveRecord,
    unsaveRecord,
    getSavedRecords,
    checkSaved,
    getSearchHistory,
    clearHistory,
  };
}
